import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IconButton } from "../control/IconButton";
import { cx } from "../cx";
import { CloseIcon } from "../icons";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  open: boolean;
  drawer?: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Buttons, right-aligned. */
  footer?: ReactNode;
  width?: "sm" | "md" | "lg";
  /** Element to focus on open; the first focusable otherwise, the panel if none. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Set false while a confirm is in flight so Esc and the scrim cannot cancel it. */
  dismissable?: boolean;
}

/**
 * Modal over a scrim, no shadow. Traps Tab inside, closes on Esc and scrim
 * click, and gives focus back to whatever opened it.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  width = "md",
  drawer = false,
  initialFocusRef,
  dismissable = true,
}: DialogProps) {
  const { t } = useTranslation("components");
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const [retained, setRetained] = useState(false);

  // Logical dismissal and focus restoration are immediate. Only the inert visual
  // drawer stays for its exit transition; reopening cancels that removal.
  useEffect(() => {
    if (open && drawer) {
      setRetained(true);
      return;
    }
    const duration =
      panel.current && drawer
        ? Number.parseFloat(
            getComputedStyle(panel.current).getPropertyValue("--sidebar-motion-panel"),
          ) || 0
        : 0;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!duration || reduce) {
      setRetained(false);
      return;
    }
    const timer = window.setTimeout(() => setRetained(false), duration + 32);
    return () => window.clearTimeout(timer);
  }, [open, drawer]);

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const target =
      initialFocusRef?.current ??
      panel.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panel.current;
    target?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, [open, initialFocusRef]);

  if (!open && !(drawer && retained)) return null;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      if (dismissable) onClose();
      return;
    }
    if (e.key !== "Tab" || !panel.current) return;
    const nodes = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (nodes.length === 0) {
      e.preventDefault();
      panel.current.focus();
      return;
    }
    const first = nodes[0] as HTMLElement;
    const last = nodes[nodes.length - 1] as HTMLElement;
    const current = document.activeElement;
    if (e.shiftKey && (current === first || current === panel.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const WIDTH = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" } as const;

  return createPortal(
    <div
      data-component="DialogScrim"
      data-drawer={drawer || undefined}
      data-open={open}
      aria-hidden={!open || undefined}
      inert={!open}
      className={cx(
        "fixed inset-0 z-40 flex bg-scrim",
        drawer ? "items-stretch justify-start" : "items-center justify-center p-6",
      )}
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal={open || undefined}
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-component="Dialog"
        data-drawer={drawer || undefined}
        data-open={open}
        className={cx(
          "flex max-h-[calc(100dvh-3rem)] w-full flex-col overflow-hidden rounded-md border border-line bg-surface outline-none",
          drawer ? "h-dvh max-h-dvh max-w-[min(88vw,24rem)] rounded-none" : WIDTH[width],
        )}
      >
        <header className="flex shrink-0 items-center gap-4 border-b border-line-soft px-5 py-3">
          <h2 id={titleId} className="flex-1 font-serif text-title-sm font-semibold text-ink">
            {title}
          </h2>
          <IconButton
            label={t("dialog.close")}
            icon={<CloseIcon size={14} />}
            size="sm"
            onClick={onClose}
            disabled={!dismissable}
          />
        </header>
        <div
          className={cx(
            "min-h-0 overflow-y-auto text-ui text-ink",
            drawer ? "flex-1" : "px-5 py-4",
          )}
        >
          {children}
        </div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line-soft px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
